import { alpha, lighten, darken } from "@mui/material";

const styles = (theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    modal: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    paper: {
      position: "relative",
      width: { xs: "92vw", sm: 540, md: 600 },
      maxHeight: "85vh",
      borderRadius: "16px",
      p: 2.5,
      display: "flex",
      flexDirection: "column",
      gap: 2,
      outline: "none",
      backgroundColor: theme.palette.background.paper,
      border: isDark
        ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
        : `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
      boxShadow: theme.shadows[16],
      overflow: "hidden",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    headerTitleContainer: {
      display: "flex",
      alignItems: "center",
      gap: 1.2,
    },
    headerIconBox: {
      width: 34,
      height: 34,
      borderRadius: "10px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: alpha(
        theme.palette.primary.main,
        isDark ? 0.2 : 0.1
      ),
      color: theme.palette.primary.main,
    },
    headerIcon: {
      fontSize: 20,
    },
    headerTitleBox: {
      display: "flex",
      alignItems: "center",
      gap: 1,
    },
    headerTitle: {
      fontWeight: 700,
      fontSize: "1.1rem",
      lineHeight: 1.2,
      color: theme.palette.text.primary,
    },
    closeButton: {
      color: theme.palette.text.secondary,
      "&:hover": {
        backgroundColor: alpha(theme.palette.text.primary, 0.08),
        color: theme.palette.text.primary,
      },
    },
    subHeader: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 1.5,
      px: 1,
      py: 0.8,
      borderRadius: "10px",
      backgroundColor: isDark
        ? alpha(theme.palette.common.white, 0.03)
        : alpha(theme.palette.common.black, 0.03),
      border: `1px solid ${alpha(
        isDark ? theme.palette.common.white : theme.palette.common.black,
        0.05
      )}`,
    },
    infoGroup: {
      display: "flex",
      alignItems: "center",
      gap: 0.6,
    },
    infoLabel: {
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      fontSize: "0.7rem",
    },
    infoValueHost: {
      fontWeight: 700,
      color: theme.palette.text.primary,
      fontSize: "0.82rem",
    },
    divider: {
      height: 16,
      my: "auto",
    },
    sessionBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      cursor: "pointer",
      px: 0.9,
      py: 0.3,
      borderRadius: "6px",
      backgroundColor: alpha(
        theme.palette.primary.main,
        isDark ? 0.18 : 0.08
      ),
      color: theme.palette.primary.main,
      fontWeight: 600,
      fontSize: "0.82rem",
      transition: "all 0.2s ease",
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    },
    sessionBadgeText: {
      fontWeight: 700,
      color: "inherit",
      fontSize: "0.82rem",
      fontFamily: "monospace",
    },
    sessionIconSuccess: {
      fontSize: 14,
      color: theme.palette.success.main,
    },
    sessionIconCopy: {
      fontSize: 13,
      opacity: 0.75,
    },
    searchInput: {
      "& .MuiOutlinedInput-root": {
        borderRadius: "10px",
        backgroundColor: isDark
          ? alpha(theme.palette.common.white, 0.04)
          : alpha(theme.palette.common.black, 0.02),
      },
    },
    searchAdornmentIcon: {
      color: theme.palette.text.secondary,
    },
    listContainer: {
      display: "flex",
      flexDirection: "column",
      gap: 0.6,
      maxHeight: "48vh",
      minHeight: "140px",
      overflowY: "auto",
      pr: 0.5,
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.text.primary, 0.15),
        borderRadius: "3px",
        "&:hover": {
          backgroundColor: alpha(theme.palette.text.primary, 0.3),
        },
      },
    },
    emptyStateContainer: {
      py: 5,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      color: theme.palette.text.secondary,
    },
    emptyStateIcon: {
      fontSize: 36,
      opacity: 0.4,
    },
    emptyStateText: {
      fontWeight: 500,
    },
    rowItem: (isEven) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 1.5,
      py: 1,
      borderRadius: "6px",
      cursor: "pointer",
      userSelect: "none",
      backgroundColor: isEven
        ? isDark
          ? lighten(theme.palette.background.paper, 0.08)
          : darken(theme.palette.background.paper, 0.06)
        : "transparent",
      transition: "all 0.15s ease",
      "&:hover": {
        backgroundColor: isDark
          ? alpha(theme.palette.primary.main, 0.22)
          : alpha(theme.palette.primary.main, 0.12),
      },
    }),
    rowLeft: {
      display: "flex",
      alignItems: "center",
      gap: 1.2,
    },
    ballOuter: {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: alpha(theme.palette.primary.main, 0.25),
    },
    ballInner: {
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
    },
    prefixText: {
      fontFamily: "monospace",
      fontSize: "0.875rem",
      fontWeight: 600,
      letterSpacing: "0.02em",
      color: theme.palette.text.primary,
    },
    rowRight: (isCopied) => ({
      display: "flex",
      alignItems: "center",
      opacity: isCopied ? 1 : 0.4,
      transition: "opacity 0.2s",
    }),
    rowIconSuccess: {
      fontSize: 16,
      color: theme.palette.success.main,
    },
    rowIconCopy: {
      fontSize: 15,
    },
    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      pt: 1.5,
      borderTop: `1px solid ${theme.palette.divider}`,
      fontSize: "0.75rem",
    },
    footerText: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    copyAllButton: (copiedAll) => ({
      fontSize: "0.75rem",
      textTransform: "none",
      py: 0.2,
      px: 1,
      color: copiedAll
        ? theme.palette.success.main
        : theme.palette.primary.main,
      fontWeight: 600,
    }),
    copyAllIconSuccess: {
      fontSize: 14,
      color: theme.palette.success.main,
    },
    copyAllIconCopy: {
      fontSize: 14,
    },
  };
};

export default styles;
